import { FC, useMemo } from 'react';

import Container from 'quarks/Container';
import Flex from 'quarks/Flex';
import Heading from 'quarks/Heading';
import Image, { ImageData } from 'quarks/Image';
import type { FlexProps } from 'quarks/interpolations/flex';
import { TextStyles } from 'quarks/styleProps/heading';

import { formatHeadingSize } from 'utils/formatHeadingSize';

import type { ContentfulComponentTrustbar } from 'graphqlTypes';

interface TrustbarProps extends FlexProps, ContentfulComponentTrustbar {
  backgroundIsDark: boolean;
}

const ComponentTrustbar: FC<TrustbarProps> = ({ heading, companySmallSize, company, backgroundIsDark, ...props }) => {
  const { headingSizeMobile, headingSizeTablet, headingSizeDesktop } = formatHeadingSize(props);

  const getCompanies = useMemo(
    () =>
      company &&
      company.map(item => (
        <Container
          key={item?.id || item?.company}
          width="100%"
          maxWidth={companySmallSize ? '140px' : '110px'}
          height={companySmallSize ? '36px' : '48px'}
          md={{ maxWidth: '150px ' }}
          lg={{ maxWidth: companySmallSize ? '165px' : '200px' }}
        >
          {backgroundIsDark ? (
            <>
              {item?.logoOnDark && (
                <Image
                  image={item?.logoOnDark as ImageData}
                  alt={item?.company || ''}
                  width="100%"
                  height="100%"
                  objectFit="contain"
                  objectPosition="center"
                />
              )}
            </>
          ) : (
            <>
              {item?.logoOnLight && (
                <Image
                  image={item?.logoOnLight as ImageData}
                  alt={item?.company || ''}
                  width="100%"
                  height="100%"
                  objectFit="contain"
                  objectPosition="center"
                />
              )}
            </>
          )}
        </Container>
      )),
    [backgroundIsDark],
  );

  return (
    <Flex flexDirection="column" justifyContent="center" textAlign="center" gap="32px" {...props}>
      {heading && (
        <Heading
          as="h2"
          fontWeight="bold"
          textColor={backgroundIsDark ? 'common-white' : 'gray-900'}
          width="100%"
          maxWidth="770px"
          marginX="auto"
          textAlign="center"
          textStyle={(headingSizeMobile as TextStyles) || 'h2'}
          md={{ textStyle: (headingSizeTablet as TextStyles) || 'h2', fontWeight: 'bold' }}
          lg={{ textStyle: (headingSizeDesktop as TextStyles) || 'h2', fontWeight: 'bold' }}
          css={{
            marginBottom: '10px',
          }}
        >
          {heading}
        </Heading>
      )}
      {company && company.length > 0 && (
        <Flex flexWrap="wrap" width="100%" height="100%" gap="32px" justifyContent="center" alignItems="center">
          {getCompanies}
        </Flex>
      )}
    </Flex>
  );
};

export default ComponentTrustbar;
